import * as amplitude from '@amplitude/unified';
import { FormikHelpers } from 'formik';
import { AuthRequestAuthCodePayload } from 'modules/auth/domain/interfaces/auth-request-auth-code-payload';
import { AuthSignInPayload } from 'modules/auth/domain/interfaces/auth-sign-in-payload';
import { authRequestCodeValidator } from 'modules/auth/domain/validators/auth-request-code.validator';
import { authSignInValidator } from 'modules/auth/domain/validators/auth-sign-in.validator';
import { authRequestAuthCode, authSignIn } from 'modules/auth/store/actions';
import {
  authStoreCheckingSelector,
  authStoreCodeRequestedSelector,
  authStoreCodeRequestingSelector,
  authStoreLoadingSelector,
} from 'modules/auth/store/selectors';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import { resetAuthChecking } from 'modules/auth/store';
import RequestAuthCodeForm from '../components/request-auth-code-form';
import SignInForm from '../components/sign-in-form';
import AuthLayout from '../layouts';

const SignInPage = () => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthCodeRequested = useSelector(authStoreCodeRequestedSelector);
  const isAuthCodeRequesting = useSelector(authStoreCodeRequestingSelector);
  const isLoading = useSelector(authStoreLoadingSelector);
  const isAuthChecking = useSelector(authStoreCheckingSelector);
  const to = (useLocation().state as { to: string } | null)?.to;

  const [username, setUsername] = useState('');
  const [isEmailCode, setIsEmailCode] = useState(false);

  const onCodeRequest = useCallback(
    (data: AuthRequestAuthCodePayload) => {
      setUsername(data.phone);
      dispatch(authRequestAuthCode(data))
        .unwrap()
        .then((response) => {
          const isEmail = response.provider === 'email';
          setIsEmailCode(isEmail);
          toast.info(isEmail ? t`Код отправлен на email` : t`Код отправлен на номер телефона`);
        });
    },
    [dispatch, t],
  );

  const onSignIn = useCallback(
    (data: AuthSignInPayload, helpers: FormikHelpers<AuthSignInPayload>) => {
      dispatch(authSignIn({ ...data, to }))
        .unwrap()
        .then(() => {
          amplitude.track('Signed In');
        })
        .catch((error) => {
          helpers.setFieldError('password', error);
        });
    },
    [dispatch, to],
  );

  const [isAuthInProcess, setIsAuthInProcess] = useState(false);

  // Заменяем useMemo на useState
  // const isAuthInProcess = useMemo(() => {
  //   return isAuthCodeRequested || isAuthChecking;
  // }, [isAuthCodeRequested, isAuthChecking]);

  // При инициализации и изменении зависимостей устанавливаем значение
  useEffect(() => {
    setIsAuthInProcess(isAuthCodeRequested || isAuthChecking);
  }, [isAuthCodeRequested, isAuthChecking]);

  // Обработчик изменения телефона
  const handleUsernameChange = useCallback(
    (value: string) => {
      if (isAuthInProcess) {
        setIsAuthInProcess(false);
        setUsername(value); // Сохраняем введенный номер телефона
        dispatch(resetAuthChecking()); // Сбрасываем состояние isAuthChecking
      }
    },
    [isAuthInProcess, dispatch],
  );

  const isSignInLoading = useMemo(() => {
    return isLoading || isAuthChecking;
  }, [isLoading, isAuthChecking]);

  return (
    <AuthLayout
      logoTo={Pages.SIGN_IN}
      formContainerProps={{ style: { height: isAuthInProcess ? 440 : 410 } }}
      title={t`Вход`}
      subtitle={t`Для организаторов спортивных мероприятий`}
      action={{
        title: t`Еще нет аккаунта?`,
        linkLabel: t`Зарегистрироваться`,
        link: Pages.SIGN_UP,
      }}
    >
      {isAuthInProcess ? (
        // После ввода кода
        <SignInForm
          initialValues={{ username, password: '' }}
          onSubmit={onSignIn}
          isLoading={isSignInLoading}
          validationSchema={authSignInValidator()}
          onUsernameChange={handleUsernameChange} // Добавляем новый проп
          onResendCode={() => onCodeRequest({ phone: username })}
          isEmailCode={isEmailCode}
        />
      ) : (
        // До ввода кода
        <RequestAuthCodeForm
          initialValues={{ phone: username }}
          onSubmit={onCodeRequest}
          isLoading={isAuthCodeRequesting}
          validationSchema={authRequestCodeValidator()}
        />
      )}
    </AuthLayout>
  );
};

export default SignInPage;
